import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PublicFooter } from "@/components/PublicChrome";
import AlfatekLogo from "@/components/AlfatekLogo";
import ProductShowcase from "@/components/ProductShowcase";
import BannerCarousel from "@/components/BannerCarousel";
import { Search, ArrowRight, Package, Sparkles, Zap, ShieldCheck, MessageSquare } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1610891015188-5369212db097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWFjaGluZXJ5JTIwZmFjdG9yeXxlbnwwfHx8fDE3ODMzOTY1MzZ8MA&ixlib=rb-4.1.0&q=85";

export default function EmBreve() {
    const [email, setEmail] = useState("");
    const [company, setCompany] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [search, setSearch] = useState("");
    const [brands, setBrands] = useState([]);
    const nav = useNavigate();

    useEffect(() => {
        let mounted = true;
        api.get("/public/brands").then((r) => {
            if (!mounted) return;
            const names = (r.data || []).map((b) => (b.name || "").toUpperCase()).filter(Boolean);
            setBrands(names);
        }).catch(() => setBrands([]));
        return () => { mounted = false; };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/leads", { email, company, source: "novidades" });
            setDone(true);
            toast.success("Recebido! Você será avisado sobre novos produtos e marcas.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao enviar");
        } finally { setLoading(false); }
    };

    const doSearch = (e) => {
        e.preventDefault();
        nav(search.trim() ? `/produtos?q=${encodeURIComponent(search.trim())}` : "/produtos");
    };

    return (
        <div className="min-h-screen bg-[#F4F5F7]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <AlfatekLogo className="h-9" />
                <div className="flex items-center gap-5 text-xs font-mono uppercase tracking-[0.2em]">
                    <Link to="/produtos" className="text-neutral-500 hover:text-brand-blue">Catálogo →</Link>
                    <Link data-testid="link-admin-topbar" to="/admin/login" className="text-neutral-500 hover:text-brand-blue">Admin →</Link>
                </div>
            </div>

            {/* HERO */}
            <section className="relative overflow-hidden border-y border-neutral-200 bg-brand-blue-deep">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-20" />
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-blue-deep via-brand-blue-deep/95 to-brand-blue/70" />
                    <div className="absolute inset-0 grid-blueprint opacity-40" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <h1 className="font-heading font-black text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight animate-fade-up" data-testid="hero-title">
                            Um só catálogo.<br />
                            <span className="text-brand-yellow">41 marcas</span> industriais confiáveis.
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg text-neutral-200 leading-relaxed animate-fade-up" style={{animationDelay: "0.1s"}}>
                            ALFATEK reúne <strong className="text-white">STIHL, WEG, 3M, Makita, DeWalt, Bosch, ESAB, SKF, NSK, Siemens</strong> e mais 31 fabricantes em uma plataforma única de busca, comparação e orçamento — para engenheiros, compradores e mantenedores.
                        </p>

                        {/* SmartFinder inline */}
                        <form onSubmit={doSearch} className="mt-10 max-w-2xl animate-fade-up" style={{animationDelay: "0.2s"}}>
                            <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-yellow mb-3">SmartFinder · buscar agora</div>
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" size={20} />
                                <input data-testid="hero-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ex: motosserra STIHL, rolamento 6205, misturador de argamassa"
                                    className="w-full h-16 pl-12 pr-40 bg-white/95 border border-white/20 text-ink placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-brand-yellow text-base" />
                                <button data-testid="hero-search-submit" className="absolute right-2 top-2 bottom-2 px-6 bg-brand-yellow text-ink font-heading font-black uppercase tracking-wider text-sm hover:bg-brand-yellow-hover flex items-center gap-2">
                                    Buscar <ArrowRight size={16} />
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="lg:col-span-4 relative">
                        <div className="bg-white/5 border border-white/10 backdrop-blur-sm p-6 font-mono text-xs text-neutral-300 leading-relaxed" data-testid="hero-spec-block">
                            <div className="uppercase tracking-widest text-brand-yellow mb-3 text-[10px]">// dados da plataforma</div>
                            <div>&gt; 41 marcas industriais parceiras</div>
                            <div>&gt; 24 categorias técnicas</div>
                            <div>&gt; 245+ produtos catalogados</div>
                            <div>&gt; busca inteligente multi-marca</div>
                            <div>&gt; orçamento sem exposição de preço</div>
                            <div>&gt; resposta em minutos: whatsapp + e-mail</div>
                            <div className="mt-4 text-brand-yellow">status: ONLINE</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE — TEXT NAMES (voltando ao formato anterior) */}
            <section className="border-b border-neutral-200 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex items-center gap-8 mb-4">
                        <span className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-400 whitespace-nowrap">Marcas parceiras</span>
                        <div className="h-px flex-1 bg-neutral-200" />
                        <Link to="/produtos" className="text-xs font-mono uppercase tracking-[0.24em] text-brand-blue hover:underline whitespace-nowrap">Ver catálogo →</Link>
                    </div>
                    <div className="overflow-hidden">
                        <div className="marquee-track">
                            {brands.length > 0 && [...brands, ...brands].map((b, i) => (
                                <span key={i} className="font-heading font-black text-2xl tracking-tight text-neutral-800 shrink-0">{b}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* BANNER CAROUSEL — gerenciado pelo admin */}
            <BannerCarousel />

            {/* PRODUCT SHOWCASE — fotos reais do catálogo (rotativo) */}
            <ProductShowcase />

            {/* HOW IT WORKS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="grid lg:grid-cols-2 gap-16 items-start">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-blue">Como funciona</div>
                        <h2 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tight">Do problema técnico<br />ao orçamento em <span className="text-brand-blue">3 passos</span>.</h2>
                        <p className="mt-6 text-neutral-600 max-w-md">Sem cadastro. Sem preços expostos. Você monta a lista técnica e nós retornamos com preço, prazo e disponibilidade pelo canal que você preferir.</p>
                    </div>
                    <div className="space-y-6">
                        {[
                            { n: "01", t: "Busque na plataforma", d: "Use o SmartFinder para achar produtos equivalentes de várias marcas lado a lado — por nome, código ou especificação.", i: Search },
                            { n: "02", t: "Monte sua lista", d: "Adicione quantos itens quiser à Lista de Orçamento — com quantidade e observação técnica por produto.", i: Package },
                            { n: "03", t: "Receba retorno", d: "Nosso vendedor recebe sua lista via WhatsApp e e-mail. Preço, prazo e substitutos técnicos vêm em minutos.", i: MessageSquare },
                        ].map((s) => (
                            <div key={s.n} className="flex gap-5">
                                <div className="w-14 h-14 bg-brand-blue-deep text-brand-yellow font-mono font-bold flex items-center justify-center shrink-0">{s.n}</div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <s.i size={18} className="text-brand-blue" />
                                        <h3 className="font-heading font-bold text-xl">{s.t}</h3>
                                    </div>
                                    <p className="mt-2 text-neutral-600">{s.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY */}
            <section className="border-y border-neutral-200 bg-brand-blue-deep text-white overflow-hidden relative">
                <div className="absolute inset-0 grid-blueprint opacity-20" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-yellow">Por que ALFATEK</div>
                    <h2 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tight max-w-3xl">Feito por especialistas em <span className="text-brand-yellow">manutenção industrial</span>. Para quem precisa de resposta técnica.</h2>
                    <div className="mt-12 grid md:grid-cols-3 gap-8">
                        {[
                            { i: Sparkles, t: "IA de equivalência (em breve)", d: "Um WEG W22 5CV e um motor Siemens de mesmo porte aparecem lado a lado — o algoritmo entende specs, não só nome." },
                            { i: Zap, t: "Velocidade real", d: "Zero cadastro. Zero preço exposto. Zero e-mail robô. Você monta e envia; um humano responde." },
                            { i: ShieldCheck, t: "Marcas com representatividade", d: "Trabalhamos com fabricantes oficiais — sem paralelo, sem cinza. Nota fiscal, garantia e ficha técnica corretas." },
                        ].map((f) => (
                            <div key={f.t}>
                                <div className="w-12 h-12 border border-brand-yellow flex items-center justify-center mb-4"><f.i size={20} className="text-brand-yellow" /></div>
                                <h3 className="font-heading font-bold text-2xl">{f.t}</h3>
                                <p className="mt-3 text-neutral-300 leading-relaxed">{f.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NEWSLETTER — novidades e lançamentos */}
            <section id="novidades" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="grid lg:grid-cols-[1fr,auto] gap-10 items-end">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-blue">Novidades e lançamentos</div>
                        <h2 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tight">Fique por dentro de <span className="text-brand-blue">novos produtos e marcas</span>.</h2>
                        <p className="mt-4 text-neutral-600 max-w-xl">Deixe seu e-mail e receba avisos pontuais quando ampliarmos o catálogo, adicionarmos marcas ou publicarmos conteúdo técnico. Sem spam, sem newsletter promocional.</p>
                    </div>
                    <div className="lg:min-w-[520px]">
                        {done ? (
                            <div data-testid="lead-success" className="border-2 border-brand-yellow bg-brand-yellow/10 p-6">
                                <div className="text-xs font-mono uppercase tracking-widest text-brand-blue mb-2">confirmado</div>
                                <div className="text-lg font-heading font-bold">Estamos com seu e-mail. Você será avisado sobre novos produtos e marcas.</div>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="bg-white border border-neutral-300 p-6">
                                <div className="grid sm:grid-cols-5 gap-2">
                                    <input data-testid="lead-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com.br" className="sm:col-span-3 h-14 px-4 bg-neutral-50 border border-neutral-300 focus:outline-none focus:border-brand-blue" />
                                    <input data-testid="lead-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Empresa (opcional)" className="sm:col-span-2 h-14 px-4 bg-neutral-50 border border-neutral-300 focus:outline-none focus:border-brand-blue" />
                                </div>
                                <button data-testid="lead-submit" disabled={loading} className="mt-3 w-full h-14 px-8 bg-brand-blue text-white font-heading font-black uppercase tracking-wider hover:bg-brand-blue-hover disabled:opacity-60">
                                    {loading ? "enviando…" : "Quero receber novidades"}
                                </button>
                                <p className="mt-3 text-xs text-neutral-500 font-mono">Sem spam. Apenas avisos de novos produtos, marcas e conteúdo técnico.</p>
                            </form>
                        )}
                    </div>
                </div>

                <div className="mt-16 border-t border-neutral-300 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="font-mono text-sm text-neutral-500">Já disponível para navegação:</div>
                    <Link data-testid="cta-preview-catalog" to="/produtos" className="inline-flex items-center gap-3 bg-brand-blue text-white px-8 h-14 font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover">
                        Explorar catálogo completo <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
